import { memo } from "react";

import { Box, Flex, Grid, Typography } from "@aircall/tractor";

import { useTranscriptItemStyle } from "../Transcription/Transcription.hooks";
import { ItemActions } from "../TranscriptionItemActions";
import { TranscriptionItemSentiment } from "../TranscriptionItemSentiment";
import { Utterance } from "../TranscriptionItemUtterance";

import * as S from "./TranscriptionItem.styled";

import { SpeakerAvatar } from "@/components/SpeakerAvatar/SpeakerAvatar";
import { formatSecondsToTime } from "@/helpers/date/date.helpers";
import { getSpeakerDisplayName } from "@/helpers/speaker/speaker.helpers";
import { trimWithEllipsis } from "@/helpers/string/string.helpers";
import { useCall } from "@/hooks/useCall/useCall";

import { useHasSentimentAnalysis } from "@/hooks/useHasSentimentAnalysis/useHasSentimentAnalysis";
import { useActiveUtterances } from "@/hooks/useScrollManager/useScrollManager";
import { useTranscriptionRule } from "@/hooks/useTranscriptionRules/useTranscriptionRules";
import { UtteranceType } from "@/states/call/utterances/utterances.utility.decl";

const THRESHOLD = 25;

type TranscriptItemProps = {
  utterance: UtteranceType;
  index: number;
  currentTime: number;
  active: boolean;
  displayName: boolean;
};

export const TranscriptItem = memo(
  ({
    utterance,
    active,
    currentTime,
    index,
    displayName,
  }: TranscriptItemProps) => {
    const { call } = useCall();
    const hasSentimentAnalysis = useHasSentimentAnalysis();
    const { isRealTimeTranscription } = useTranscriptionRule();

    const activeUtterances = useActiveUtterances();

    const isActive = (index: number) => activeUtterances.includes(index);

    const sentiment = call?.utteranceSentiments?.get(index)?.value;
    const { color, shouldRenderAvatar } = useTranscriptItemStyle(
      utterance.channel
    );

    if (!call) {
      return null;
    }

    const text = getSpeakerDisplayName(
      call.speakers?.get(utterance.channel)?.fullName,
      call.speakers?.get(utterance.channel)?.number
    );
    const { startTime } = utterance;

    // Format the Date object to get minutes and seconds
    const formattedTime = formatSecondsToTime(startTime);

    return (
      <S.Container className="transcript_item">
        <S.AvatarWrapper
          active={isActive(index)}
          data-test={`transcript-item-avatar-${utterance.channel}`}
        >
          {/* For a voicemail utterance, we don't render the avatar.
          In this case, it's important to leave this div empty, to make sure the Utterance take the second column, 
          so Utterance Section can be centered */}
          {shouldRenderAvatar && (
            <SpeakerAvatar
              channel={utterance.channel}
              shouldDisplayTooltip={false}
            />
          )}
        </S.AvatarWrapper>
        <Grid
          gridAutoFlow="column"
          gridTemplateColumns="auto 1fr auto"
          columnGap="8px"
          h="100%"
          justifyContent="start"
          alignItems="center"
          data-test={`transcript-item-utterance-${index}`}
          // take a minimum height as the left avatar
          minH="24px"
        >
          <Box backgroundColor={color} w="2px" borderRadius="2px" h="100%" />

          <Flex flexDirection="column" gap="xxxs">
            {displayName && (
              <Typography color={color} variant="bodySemiboldM">
                {text && trimWithEllipsis(text, THRESHOLD)}
              </Typography>
            )}
            <Utterance
              active={active}
              currentTime={currentTime}
              entries={utterance.entries}
              index={index}
            />
          </Flex>
          {hasSentimentAnalysis && (
            <TranscriptionItemSentiment sentiment={sentiment} />
          )}
          {!isRealTimeTranscription && (
            <ItemActions
              displayedStartTime={formattedTime}
              utterance={utterance}
            />
          )}
        </Grid>
      </S.Container>
    );
  },
  (prevProps, nextProps) => !prevProps.active && !nextProps.active
);
